import { GENDERS, ID_PROOF_TYPES } from '@gty/common';
import type { DocumentPassengerDto, Gender, UpdateDocumentPassengerDto } from '@gty/common';

import { Field, Select, TextInput, selectAllOnFocus } from '../ui/Modal';

/**
 * The passenger sub-form both document editors mount.
 *
 * ## WHY THIS EXISTS AT ALL
 *
 * The flow doc's own example of a document correction — staff spot a misspelled
 * passenger name on the voucher and fix it — was impossible: no passenger field
 * was editable on either document, and the voucher had no passenger rows of its
 * own to edit.
 *
 * ## WHAT IT EDITS — AND WHAT IT DOES NOT
 *
 * THIS DOCUMENT ONLY. Correcting a name here does not touch the booking's
 * passenger record, and does not touch the other document's copy: the voucher
 * and the invoice hold separate snapshots. That is the same principle the
 * invoice's buyer block already states — a document must not mutate when the
 * underlying record later changes — and it is the reversible choice. It is also
 * surprising, so both editors say it on screen rather than leaving staff to
 * assume one fix covered everything.
 *
 * Only the fields these documents actually PRINT are here: the passenger table
 * on both sheets is `# · Name · Age · Gender · ID Proof Type · ID Proof Number`.
 * Nothing else was added — a field that renders nowhere is a field that silently
 * disagrees with the paper.
 *
 * NO ADD AND NO REMOVE. Changing how many people travel changes `guestCount` and
 * the party the booking was priced for; that is a booking decision, not a
 * document correction, and the server refuses a row id it does not already hold.
 */

/**
 * A passenger row as FORM STRINGS.
 *
 * `age` lives as a string for the same reason `adults` does on the voucher: a
 * numeric binding cannot represent the empty box the user sees mid-edit.
 * `gender` uses `''` for "not stated", which is a real state — staff are not
 * always asked, at booking time or here, and a row snapshotted from a
 * booking copies that absence rather than guessing at it.
 */
export interface PassengerDraft {
  id: string;
  name: string;
  age: string;
  gender: '' | Gender;
  idType: string;
  /**
   * UI-ONLY, never diffed or sent: true the moment "Other" is explicitly
   * picked from the ID-type dropdown, so the text box stays open even while
   * `idType` is still empty (nothing typed yet) — without this, an empty
   * `idType` would make the dropdown's derived value fall back to "Not
   * stated" on the very next render, closing the box the user just opened.
   */
  idTypeOther: boolean;
  idNumber: string;
}

/**
 * Which option the ID-type dropdown should show selected — one of the five
 * known types verbatim, "Other" for anything else non-empty (a legacy
 * spelling, or a genuinely custom value), and "" (Not stated) only when
 * nothing has been entered at all.
 */
function idProofDropdownValue(idType: string): string {
  if (idType === '') return '';
  return (ID_PROOF_TYPES as readonly string[]).includes(idType) ? idType : 'Other';
}

export function toPassengerDrafts(rows: readonly DocumentPassengerDto[]): PassengerDraft[] {
  return rows.map((p) => ({
    id: p.id,
    name: p.name,
    age: String(p.age),
    gender: p.gender ?? '',
    idType: p.idType,
    idTypeOther: false,
    idNumber: p.idNumber,
  }));
}

/**
 * ONLY THE ROWS AND FIELDS THAT CHANGED.
 *
 * Exported for its own test. A row whose every field matches the server is
 * omitted entirely, so a six-passenger voucher saved after a one-name fix sends
 * one row — the server writes one edit-trail entry per save listing field paths,
 * and sending all six would record thirty changes and bury the correction.
 *
 * Written out field by field rather than looping a key list, for the reason
 * `diffInvoice` gives: the loop needs a cast to satisfy the optional-property
 * types, and a cast is exactly what lets an unintended field through.
 */
export function diffPassengers(
  original: readonly DocumentPassengerDto[],
  drafts: readonly PassengerDraft[],
): UpdateDocumentPassengerDto[] {
  const byId = new Map(original.map((row) => [row.id, row]));
  const patches: UpdateDocumentPassengerDto[] = [];

  for (const draft of drafts) {
    const row = byId.get(draft.id);
    if (row === undefined) continue;

    const patch: UpdateDocumentPassengerDto = { id: draft.id };
    let changed = false;

    if (draft.name !== row.name) {
      patch.name = draft.name;
      changed = true;
    }

    /**
     * A half-typed or emptied age box is not an instruction to write NaN into
     * the document — it is left out of the patch entirely, exactly as
     * `diffInvoice` treats a non-numeric money box.
     */
    const age = Number(draft.age);
    if (draft.age !== '' && Number.isFinite(age) && age !== row.age) {
      patch.age = age;
      changed = true;
    }

    // `''` is a deliberate NULL — "not stated" — so it is SENT rather than
    // treated as unchanged, the same way an emptied balance-due date is.
    const gender = draft.gender === '' ? null : draft.gender;
    if (gender !== row.gender) {
      patch.gender = gender;
      changed = true;
    }

    if (draft.idType !== row.idType) {
      patch.idType = draft.idType;
      changed = true;
    }
    if (draft.idNumber !== row.idNumber) {
      patch.idNumber = draft.idNumber;
      changed = true;
    }

    if (changed) patches.push(patch);
  }

  return patches;
}

/**
 * The rendered sub-form.
 *
 * `disabled` is inherited from the enclosing `<fieldset>` in both editors, so
 * these inputs grey out under a lock and for a view-only role without this
 * component knowing anything about permissions.
 */
export function DocumentPassengerFields({
  document,
  drafts,
  onChange,
}: {
  /** Names the OTHER document in the caption, so the warning is concrete. */
  document: 'voucher' | 'invoice';
  drafts: readonly PassengerDraft[];
  onChange: (index: number, changes: Partial<PassengerDraft>) => void;
}): JSX.Element {
  const other = document === 'voucher' ? 'invoice' : 'voucher';

  if (drafts.length === 0) {
    return (
      <div className="border-t border-neutral-200 px-5 py-4">
        <h3 className="text-[11px] font-semibold uppercase leading-none tracking-caps text-neutral-600">
          Passengers
        </h3>
        <p className="mt-2 text-[12px] leading-[1.5] text-neutral-600">
          This {document} carries no passenger rows. They are snapshotted from the booking when
          the document is raised — add the travellers to the booking and the next document raised
          will carry them.
        </p>
      </div>
    );
  }

  return (
    <div className="border-t border-neutral-200 px-5 py-4">
      <h3 className="text-[11px] font-semibold uppercase leading-none tracking-caps text-neutral-600">
        Passengers
      </h3>
      {/*
        STATED, NOT IMPLIED. The document-only rule is the surprising half of
        this feature: staff will otherwise assume one correction fixed the name
        everywhere. Saying it here is the cost of not silently rewriting the
        booking — and of not rewriting what an already-downloaded PDF claimed.
      */}
      <p className="mt-1.5 text-[11.5px] leading-[1.5] text-neutral-600">
        These are this {document}’s own rows, as printed on it. Correcting a name here does not
        change the booking’s passenger list or the {other} — each holds its own snapshot, so fix
        it in both places if both have been sent out.
      </p>

      <div className="mt-3 grid gap-4">
        {drafts.map((p, index) => (
          <div
            key={p.id}
            className="grid gap-3 rounded-[8px] border border-neutral-300 p-3.5 sm:grid-cols-2"
          >
            <div className="sm:col-span-2">
              {/*
                Labelled per row ("Passenger 1 name") rather than by a column
                header, so a screen reader and a keyboard user both know which
                traveller they are editing.
              */}
              <Field label={`Passenger ${index + 1} name`}>
                <TextInput
                  value={p.name}
                  maxLength={120}
                  onChange={(e) => {
                    onChange(index, { name: e.target.value });
                  }}
                />
              </Field>
            </div>

            <Field label={`Passenger ${index + 1} age`}>
              <TextInput
                type="number"
                min={0}
                max={120}
                value={p.age}
                onFocus={selectAllOnFocus}
                onChange={(e) => {
                  onChange(index, { age: e.target.value });
                }}
              />
            </Field>

            <Field label={`Passenger ${index + 1} gender`} hint="Blank prints as “—”.">
              <Select
                value={p.gender}
                onChange={(e) => {
                  onChange(index, { gender: e.target.value as '' | Gender });
                }}
              >
                <option value="">Not stated</option>
                {GENDERS.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label={`Passenger ${index + 1} ID proof type`}>
              <Select
                value={p.idTypeOther ? 'Other' : idProofDropdownValue(p.idType)}
                onChange={(e) => {
                  const isOther = e.target.value === 'Other';
                  onChange(index, { idType: isOther ? '' : e.target.value, idTypeOther: isOther });
                }}
              >
                <option value="">Not stated</option>
                {ID_PROOF_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
              {(p.idTypeOther || idProofDropdownValue(p.idType) === 'Other') && (
                <TextInput
                  className="mt-1.5"
                  value={p.idType}
                  maxLength={60}
                  placeholder="Enter ID type"
                  aria-label={`Passenger ${index + 1} other ID type`}
                  onChange={(e) => {
                    onChange(index, { idType: e.target.value });
                  }}
                />
              )}
            </Field>

            <Field label={`Passenger ${index + 1} ID proof number`}>
              <TextInput
                value={p.idNumber}
                maxLength={60}
                onChange={(e) => {
                  onChange(index, { idNumber: e.target.value });
                }}
              />
            </Field>
          </div>
        ))}
      </div>
    </div>
  );
}
